import {type FocusEvent, type KeyboardEvent, type MouseEvent, type RefObject, useCallback, useEffect, useId, useMemo, useRef, useState} from "react";

import type {
    ISearchComboboxGroupDomProps,
    ISearchComboboxInputDomProps,
    ISearchComboboxListboxDomProps,
    ISearchComboboxOptionDomProps
} from "../types/ISearchComboboxDomProps";
import type {ISearchComboboxNavItem} from "../types/ISearchComboboxNavItem";
import type {IUseSearchComboboxOptions, IUseSearchComboboxResult, SearchComboboxInputUserProps} from "../types/IUseSearchComboboxOptions";
import type {SearchComboboxOpenChangeReason} from "../types/SearchComboboxOpenChangeReason";
import {useIsomorphicLayoutEffect} from "./use_isomorphic_layout_effect";
import {useLatestRef} from "./use_latest_ref";

type ActiveSource = "keyboard" | "pointer" | "api";

/** The user took over the active item (arrows, mouse, `setActiveId`); valid only while `inputValue` stays the same. */
interface IUserActive {
    id: string | null;
    inputValue: string;
    source: ActiveSource;
}

interface IPointerPosition {
    x: number;
    y: number;
}

const ID_SAFE_CHAR = /[A-Za-z0-9-]/;

/**
 * Item ids may hold spaces, "|" or Polish letters ("address:warszawa|ulica puławska|257"), which are not valid in an
 * IDREF. Every other character (the escape `_` included) becomes `_<char code in base 36>_`, so the mapping stays
 * injective and the result is safe in CSS selectors too.
 */
const encodeIdPart = (value: string): string => {
    let encoded = "";

    for (let index = 0; index < value.length; index++) {
        const char = value.charAt(index);
        encoded += ID_SAFE_CHAR.test(char) ? char : `_${value.charCodeAt(index).toString(36)}_`;
    }

    return encoded;
};

const preventDefault = (event: {preventDefault: () => void}) => event.preventDefault();

const getEventTarget = (event: Event): EventTarget | null => (typeof event.composedPath === "function" ? event.composedPath()[0] : null) ?? event.target;

const isInsideRefs = (target: EventTarget | null, refs: readonly RefObject<HTMLElement | null>[]): boolean =>
    target instanceof Node && refs.some((ref) => Boolean(ref.current?.contains(target)));

const findItemIndex = (items: readonly ISearchComboboxNavItem[], id: string | null): number => (id === null ? -1 : items.findIndex((item) => item.id === id));

/** Index of the first enabled item from `startIndex` on, walking by `step` (around the ends when `wrap`); -1 when none. */
const findEnabledIndex = (items: readonly ISearchComboboxNavItem[], startIndex: number, step: 1 | -1, wrap: boolean): number => {
    for (let offset = 0; offset < items.length; offset++) {
        const position = startIndex + offset * step;
        if (!wrap && (position < 0 || position >= items.length)) {
            return -1;
        }

        const index = ((position % items.length) + items.length) % items.length;
        if (!items[index].isDisabled) {
            return index;
        }
    }

    return -1;
};

/** A disabled candidate (a row whose data turned out missing) hands the position to the next enabled item after it. */
const resolveActiveId = (
    items: readonly ISearchComboboxNavItem[],
    itemsById: ReadonlyMap<string, ISearchComboboxNavItem>,
    id: string | null
): string | null => {
    const item = id === null ? undefined : itemsById.get(id);
    if (!item) {
        return null;
    }
    if (!item.isDisabled) {
        return item.id;
    }

    const nextIndex = findEnabledIndex(items, findItemIndex(items, item.id) + 1, 1, false);

    return nextIndex === -1 ? null : items[nextIndex].id;
};

/**
 * WAI-ARIA 1.2 combobox with a listbox popup and `aria-activedescendant` (focus never leaves the input).
 *
 * Keyboard: ↑/↓ move with wrap-around (and open a closed panel), Home/End jump to the first/last item (only with an
 * empty input - with text they move the caret), Enter selects the active item or calls `onSubmitWithoutActive`, Esc
 * and Tab close, Delete removes the active item (only with an empty input, so it still edits text otherwise),
 * Shift+Delete removes it at any time.
 *
 * Active item: `autoActiveId` wins until the user takes over with arrows, the mouse or `setActiveId`; typing ends the
 * take-over. An id missing from `items` is never active. Keyboard, API and automatic changes scroll the item into view,
 * hover does not (the list must not move under the cursor). Disabled items are skipped by every key, ignore hover and
 * clicks, and an active id that becomes disabled moves on to the next enabled item.
 *
 * Open state is controlled: the hook only asks via `onOpenChange` - on focus (also a focus that arrived before the hook
 * mounted, e.g. before hydration), click, typing and arrows, and to close on Esc, Tab and a pointer down or focus move
 * outside every `rootRefs` element (portal content included).
 */
export const useSearchCombobox = (options: IUseSearchComboboxOptions): IUseSearchComboboxResult => {
    const {isOpen, items, autoActiveId = null, inputValue, idPrefix, listboxLabel} = options;

    const reactId = useId();
    const prefix = idPrefix ?? `search-combobox-${reactId.replace(/[^A-Za-z0-9_-]/g, "")}`;
    const inputId = `${prefix}-input`;
    const listboxId = `${prefix}-listbox`;

    const [userActive, setUserActive] = useState<IUserActive | null>(null);
    const lastPointerRef = useRef<IPointerPosition | null>(null);
    const pendingOpenRef = useRef<boolean | null>(null);

    const itemsById = useMemo(() => {
        const map = new Map<string, ISearchComboboxNavItem>();
        items.forEach((item) => map.set(item.id, item));
        return map;
    }, [items]);

    const isUserControlled = userActive !== null && userActive.inputValue === inputValue;
    const candidateId = isUserControlled ? userActive.id : autoActiveId;
    const activeId = isOpen ? resolveActiveId(items, itemsById, candidateId) : null;
    const activeItem = activeId !== null ? itemsById.get(activeId) : undefined;
    const shouldScrollToActive = !isUserControlled || userActive.source !== "pointer";

    const latestRef = useLatestRef({isOpen, inputValue, activeItem, options});

    const getOptionDomId = useCallback((id: string) => `${prefix}-option-${encodeIdPart(id)}`, [prefix]);
    const getGroupLabelId = useCallback((groupKey: string) => `${prefix}-group-${encodeIdPart(groupKey)}`, [prefix]);

    // A request counts as pending until the parent commits (then `isOpen` is authoritative) or the tick ends (a parent
    // that ignored the request is asked again on the next interaction).
    useIsomorphicLayoutEffect(() => {
        pendingOpenRef.current = null;
    });

    /**
     * Blur and focusin (or pointerdown and focusin) can both report "outside" before the parent re-renders; the
     * pending flag keeps it to one `onOpenChange` call.
     */
    const requestOpenChange = useCallback((nextIsOpen: boolean, reason: SearchComboboxOpenChangeReason) => {
        const currentIsOpen = pendingOpenRef.current ?? latestRef.current.isOpen;

        if (currentIsOpen === nextIsOpen) {
            return;
        }

        pendingOpenRef.current = nextIsOpen;
        Promise.resolve().then(() => {
            pendingOpenRef.current = null;
        });
        latestRef.current.options.onOpenChange(nextIsOpen, reason);
    }, []);

    const setActiveId = useCallback((id: string | null) => {
        setUserActive({id, inputValue: latestRef.current.inputValue, source: "api"});
    }, []);

    const reset = useCallback(() => setUserActive(null), []);

    const submit = useCallback(() => {
        const {activeItem: currentActiveItem, options: currentOptions} = latestRef.current;

        if (currentActiveItem) {
            currentActiveItem.onSelect();
        } else {
            currentOptions.onSubmitWithoutActive();
        }
    }, []);

    // A focus that came before the mount (a click before hydration) fired no React event: ask as if it came now.
    // The input's id is stable across hydration (`useId` or `idPrefix`), so the DOM tells whether it is focused.
    useEffect(() => {
        if (document.activeElement?.id === inputId) {
            requestOpenChange(true, "focus");
        }
    }, []);

    // A closed panel starts over: the next open follows `autoActiveId` again.
    useEffect(() => {
        if (!isOpen) {
            setUserActive((previous) => (previous === null ? previous : null));
        }
    }, [isOpen]);

    useIsomorphicLayoutEffect(() => {
        if (activeId === null || !shouldScrollToActive) {
            return;
        }

        const element = document.getElementById(getOptionDomId(activeId));

        // jsdom does not implement scrollIntoView.
        if (element && typeof element.scrollIntoView === "function") {
            element.scrollIntoView({block: "nearest"});
        }
    }, [activeId]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const closeWhenOutside = (event: Event) => {
            if (!isInsideRefs(getEventTarget(event), latestRef.current.options.rootRefs)) {
                requestOpenChange(false, "outside");
            }
        };

        // Capture phase: a `stopPropagation` somewhere on the page must not keep the panel open.
        document.addEventListener("pointerdown", closeWhenOutside, true);
        document.addEventListener("focusin", closeWhenOutside, true);

        return () => {
            document.removeEventListener("pointerdown", closeWhenOutside, true);
            document.removeEventListener("focusin", closeWhenOutside, true);
        };
    }, [isOpen, requestOpenChange]);

    const activateByKeyboard = (id: string | null) => setUserActive({id, inputValue, source: "keyboard"});

    const moveActive = (step: 1 | -1) => {
        const currentIndex = findItemIndex(items, activeId);
        const startIndex = currentIndex === -1 ? (step === 1 ? 0 : items.length - 1) : currentIndex + step;
        const nextIndex = findEnabledIndex(items, startIndex, step, true);

        if (nextIndex !== -1) {
            activateByKeyboard(items[nextIndex].id);
        }
    };

    const removeActive = (): boolean => {
        if (!activeItem?.onRemove) {
            return false;
        }

        // The removed row disappears from `items`; keep the keyboard position on the row that takes its place.
        const index = findItemIndex(items, activeItem.id);
        const nextIndex = findEnabledIndex(items, index + 1, 1, false);
        const neighbourIndex = nextIndex === -1 ? findEnabledIndex(items, index - 1, -1, false) : nextIndex;

        activateByKeyboard(neighbourIndex === -1 ? null : items[neighbourIndex].id);
        activeItem.onRemove();

        return true;
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        // Keys confirming an IME composition belong to the composition.
        if (event.nativeEvent.isComposing) {
            return;
        }

        switch (event.key) {
            case "ArrowDown":
            case "ArrowUp":
                event.preventDefault();
                if (isOpen) {
                    moveActive(event.key === "ArrowDown" ? 1 : -1);
                } else {
                    requestOpenChange(true, "arrow");
                }
                return;
            case "Home":
            case "End": {
                // With text in the field the keys move the caret (editable combobox); Shift/modifier variants keep their native selection meaning.
                if (!isOpen || inputValue !== "" || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
                    return;
                }
                const edgeIndex = event.key === "Home" ? findEnabledIndex(items, 0, 1, false) : findEnabledIndex(items, items.length - 1, -1, false);
                if (edgeIndex === -1) {
                    return;
                }
                event.preventDefault();
                activateByKeyboard(items[edgeIndex].id);
                return;
            }
            case "Enter":
                event.preventDefault();
                submit();
                return;
            case "Escape":
                if (!isOpen) {
                    return;
                }
                event.preventDefault();
                // An enclosing modal must not close together with the panel.
                event.stopPropagation();
                requestOpenChange(false, "escape");
                return;
            case "Tab":
                if (isOpen) {
                    requestOpenChange(false, "tab");
                }
                return;
            case "Delete":
                if ((event.shiftKey || inputValue === "") && removeActive()) {
                    event.preventDefault();
                }
                return;
        }
    };

    const getInputProps = <TProps extends SearchComboboxInputUserProps>(userProps?: TProps) => {
        const {onChange, onKeyDown, onFocus, onClick, onBlur, ...restProps} = (userProps ?? {}) as TProps;

        const inputProps: ISearchComboboxInputDomProps = {
            id: inputId,
            role: "combobox",
            autoComplete: "off",
            "aria-autocomplete": "list",
            "aria-expanded": isOpen,
            "aria-controls": listboxId,
            "aria-activedescendant": activeId !== null ? getOptionDomId(activeId) : undefined,
            onChange: (event) => {
                onChange?.(event);
                if (!event.defaultPrevented) {
                    requestOpenChange(true, "typing");
                }
            },
            onKeyDown: (event) => {
                onKeyDown?.(event);
                if (!event.defaultPrevented) {
                    handleKeyDown(event);
                }
            },
            onFocus: (event) => {
                onFocus?.(event);
                if (!event.defaultPrevented) {
                    requestOpenChange(true, "focus");
                }
            },
            onClick: (event) => {
                onClick?.(event);
                if (!event.defaultPrevented) {
                    requestOpenChange(true, "click");
                }
            },
            onBlur: (event: FocusEvent<HTMLInputElement>) => {
                onBlur?.(event);
                // No related target means the window lost focus or a non-focusable spot was clicked - the pointer
                // listener handles the latter, the former keeps the panel for when the user comes back.
                if (!event.defaultPrevented && event.relatedTarget && !isInsideRefs(event.relatedTarget, latestRef.current.options.rootRefs)) {
                    requestOpenChange(false, "outside");
                }
            }
        };

        return {...restProps, ...inputProps} as Omit<TProps, keyof ISearchComboboxInputDomProps> & ISearchComboboxInputDomProps;
    };

    const getListboxProps = (): ISearchComboboxListboxDomProps => ({
        id: listboxId,
        role: "listbox",
        tabIndex: -1,
        "aria-label": listboxLabel,
        onMouseDown: preventDefault
    });

    const getOptionProps = (id: string): ISearchComboboxOptionDomProps => {
        const item = itemsById.get(id);
        const optionProps: ISearchComboboxOptionDomProps = {
            id: getOptionDomId(id),
            role: "option",
            "aria-selected": activeId === id,
            onMouseDown: preventDefault,
            onMouseMove: (event: MouseEvent<HTMLElement>) => {
                if (!item || item.isDisabled) {
                    return;
                }

                // A list scrolling under a still cursor may emit mousemove with unchanged coordinates - not a hover.
                const lastPointer = lastPointerRef.current;
                if (lastPointer && lastPointer.x === event.clientX && lastPointer.y === event.clientY) {
                    return;
                }
                lastPointerRef.current = {x: event.clientX, y: event.clientY};

                if (activeId !== id || !isUserControlled) {
                    setUserActive({id, inputValue, source: "pointer"});
                }
            },
            onClick: () => {
                if (item && !item.isDisabled) {
                    item.onSelect();
                }
            }
        };

        // role="option" does not support aria-expanded (ARIA 1.2); the consumer names the state in the row's text.
        if (item?.isExpanded !== undefined) {
            optionProps["data-expanded"] = item.isExpanded;
        }
        if (item?.isDisabled) {
            optionProps["aria-disabled"] = true;
        }

        return optionProps;
    };

    const getGroupProps = (labelId: string): ISearchComboboxGroupDomProps => ({role: "group", "aria-labelledby": labelId});

    return {
        inputId,
        listboxId,
        activeId,
        setActiveId,
        reset,
        submit,
        getInputProps,
        getListboxProps,
        getOptionProps,
        getGroupProps,
        getGroupLabelId,
        getOptionDomId
    };
};
import {type FocusEvent, type KeyboardEvent, type MouseEvent, type RefObject, useCallback, useEffect, useId, useMemo, useRef, useState} from "react";

import type {
    ISearchComboboxGroupDomProps,
    ISearchComboboxInputDomProps,
    ISearchComboboxListboxDomProps,
    ISearchComboboxOptionDomProps
} from "../types/ISearchComboboxDomProps";
import type {ISearchComboboxNavItem} from "../types/ISearchComboboxNavItem";
import type {IUseSearchComboboxOptions, IUseSearchComboboxResult, SearchComboboxInputUserProps} from "../types/IUseSearchComboboxOptions";
import type {SearchComboboxOpenChangeReason} from "../types/SearchComboboxOpenChangeReason";
import {useIsomorphicLayoutEffect} from "./use_isomorphic_layout_effect";
import {useLatestRef} from "./use_latest_ref";

type ActiveSource = "keyboard" | "pointer" | "api";

/** The user took over the active item (arrows, mouse, `setActiveId`); valid only while `inputValue` stays the same. */
interface IUserActive {
    id: string | null;
    inputValue: string;
    source: ActiveSource;
}

interface IPointerPosition {
    x: number;
    y: number;
}

const ID_SAFE_CHAR = /[A-Za-z0-9-]/;

/**
 * Item ids may hold spaces, "|" or Polish letters ("address:warszawa|ulica puławska|257"), which are not valid in an
 * IDREF. Every other character (the escape `_` included) becomes `_<char code in base 36>_`, so the mapping stays
 * injective and the result is safe in CSS selectors too.
 */
const encodeIdPart = (value: string): string => {
    let encoded = "";

    for (let index = 0; index < value.length; index++) {
        const char = value.charAt(index);
        encoded += ID_SAFE_CHAR.test(char) ? char : `_${value.charCodeAt(index).toString(36)}_`;
    }

    return encoded;
};

const preventDefault = (event: {preventDefault: () => void}) => event.preventDefault();

const getEventTarget = (event: Event): EventTarget | null => (typeof event.composedPath === "function" ? event.composedPath()[0] : null) ?? event.target;

const isInsideRefs = (target: EventTarget | null, refs: readonly RefObject<HTMLElement | null>[]): boolean =>
    target instanceof Node && refs.some((ref) => Boolean(ref.current?.contains(target)));

const findItemIndex = (items: readonly ISearchComboboxNavItem[], id: string | null): number => (id === null ? -1 : items.findIndex((item) => item.id === id));

/** Index of the first enabled item from `startIndex` on, walking by `step` (around the ends when `wrap`); -1 when none. */
const findEnabledIndex = (items: readonly ISearchComboboxNavItem[], startIndex: number, step: 1 | -1, wrap: boolean): number => {
    for (let offset = 0; offset < items.length; offset++) {
        const position = startIndex + offset * step;
        if (!wrap && (position < 0 || position >= items.length)) {
            return -1;
        }

        const index = ((position % items.length) + items.length) % items.length;
        if (!items[index].isDisabled) {
            return index;
        }
    }

    return -1;
};

/** A disabled candidate (a row whose data turned out missing) hands the position to the next enabled item after it. */
const resolveActiveId = (
    items: readonly ISearchComboboxNavItem[],
    itemsById: ReadonlyMap<string, ISearchComboboxNavItem>,
    id: string | null
): string | null => {
    const item = id === null ? undefined : itemsById.get(id);
    if (!item) {
        return null;
    }
    if (!item.isDisabled) {
        return item.id;
    }

    const nextIndex = findEnabledIndex(items, findItemIndex(items, item.id) + 1, 1, false);

    return nextIndex === -1 ? null : items[nextIndex].id;
};

/**
 * WAI-ARIA 1.2 combobox with a listbox popup and `aria-activedescendant` (focus never leaves the input).
 *
 * Keyboard: ↑/↓ move with wrap-around (and open a closed panel), Home/End jump to the first/last item (only with an
 * empty input - with text they move the caret), Enter selects the active item or calls `onSubmitWithoutActive`, Esc
 * and Tab close, Delete removes the active item (only with an empty input, so it still edits text otherwise),
 * Shift+Delete removes it at any time.
 *
 * Active item: `autoActiveId` wins until the user takes over with arrows, the mouse or `setActiveId`; typing ends the
 * take-over. An id missing from `items` is never active. Keyboard, API and automatic changes scroll the item into view,
 * hover does not (the list must not move under the cursor). Disabled items are skipped by every key, ignore hover and
 * clicks, and an active id that becomes disabled moves on to the next enabled item.
 *
 * Open state is controlled: the hook only asks via `onOpenChange` - on focus (also a focus that arrived before the hook
 * mounted, e.g. before hydration), click, typing and arrows, and to close on Esc, Tab and a pointer down or focus move
 * outside every `rootRefs` element (portal content included).
 */
export const useSearchCombobox = (options: IUseSearchComboboxOptions): IUseSearchComboboxResult => {
    const {isOpen, items, autoActiveId = null, inputValue, idPrefix, listboxLabel} = options;

    const reactId = useId();
    const prefix = idPrefix ?? `search-combobox-${reactId.replace(/[^A-Za-z0-9_-]/g, "")}`;
    const inputId = `${prefix}-input`;
    const listboxId = `${prefix}-listbox`;

    const [userActive, setUserActive] = useState<IUserActive | null>(null);
    const lastPointerRef = useRef<IPointerPosition | null>(null);
    const pendingOpenRef = useRef<boolean | null>(null);

    const itemsById = useMemo(() => {
        const map = new Map<string, ISearchComboboxNavItem>();
        items.forEach((item) => map.set(item.id, item));
        return map;
    }, [items]);

    const isUserControlled = userActive !== null && userActive.inputValue === inputValue;
    const candidateId = isUserControlled ? userActive.id : autoActiveId;
    const activeId = isOpen ? resolveActiveId(items, itemsById, candidateId) : null;
    const activeItem = activeId !== null ? itemsById.get(activeId) : undefined;
    const shouldScrollToActive = !isUserControlled || userActive.source !== "pointer";

    const latestRef = useLatestRef({isOpen, inputValue, activeItem, options});

    const getOptionDomId = useCallback((id: string) => `${prefix}-option-${encodeIdPart(id)}`, [prefix]);
    const getGroupLabelId = useCallback((groupKey: string) => `${prefix}-group-${encodeIdPart(groupKey)}`, [prefix]);

    // A request counts as pending until the parent commits (then `isOpen` is authoritative) or the tick ends (a parent
    // that ignored the request is asked again on the next interaction).
    useIsomorphicLayoutEffect(() => {
        pendingOpenRef.current = null;
    });

    /**
     * Blur and focusin (or pointerdown and focusin) can both report "outside" before the parent re-renders; the
     * pending flag keeps it to one `onOpenChange` call.
     */
    const requestOpenChange = useCallback((nextIsOpen: boolean, reason: SearchComboboxOpenChangeReason) => {
        const currentIsOpen = pendingOpenRef.current ?? latestRef.current.isOpen;

        if (currentIsOpen === nextIsOpen) {
            return;
        }

        pendingOpenRef.current = nextIsOpen;
        Promise.resolve().then(() => {
            pendingOpenRef.current = null;
        });
        latestRef.current.options.onOpenChange(nextIsOpen, reason);
    }, []);

    const setActiveId = useCallback((id: string | null) => {
        setUserActive({id, inputValue: latestRef.current.inputValue, source: "api"});
    }, []);

    const reset = useCallback(() => setUserActive(null), []);

    const submit = useCallback(() => {
        const {activeItem: currentActiveItem, options: currentOptions} = latestRef.current;

        if (currentActiveItem) {
            currentActiveItem.onSelect();
        } else {
            currentOptions.onSubmitWithoutActive();
        }
    }, []);

    // A focus that came before the mount (a click before hydration) fired no React event: ask as if it came now.
    // The input's id is stable across hydration (`useId` or `idPrefix`), so the DOM tells whether it is focused.
    useEffect(() => {
        if (document.activeElement?.id === inputId) {
            requestOpenChange(true, "focus");
        }
    }, []);

    // A closed panel starts over: the next open follows `autoActiveId` again.
    useEffect(() => {
        if (!isOpen) {
            setUserActive((previous) => (previous === null ? previous : null));
        }
    }, [isOpen]);

    useIsomorphicLayoutEffect(() => {
        if (activeId === null || !shouldScrollToActive) {
            return;
        }

        const element = document.getElementById(getOptionDomId(activeId));

        // jsdom does not implement scrollIntoView.
        if (element && typeof element.scrollIntoView === "function") {
            element.scrollIntoView({block: "nearest"});
        }
    }, [activeId]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const closeWhenOutside = (event: Event) => {
            if (!isInsideRefs(getEventTarget(event), latestRef.current.options.rootRefs)) {
                requestOpenChange(false, "outside");
            }
        };

        // Capture phase: a `stopPropagation` somewhere on the page must not keep the panel open.
        document.addEventListener("pointerdown", closeWhenOutside, true);
        document.addEventListener("focusin", closeWhenOutside, true);

        return () => {
            document.removeEventListener("pointerdown", closeWhenOutside, true);
            document.removeEventListener("focusin", closeWhenOutside, true);
        };
    }, [isOpen, requestOpenChange]);

    const activateByKeyboard = (id: string | null) => setUserActive({id, inputValue, source: "keyboard"});

    const moveActive = (step: 1 | -1) => {
        const currentIndex = findItemIndex(items, activeId);
        const startIndex = currentIndex === -1 ? (step === 1 ? 0 : items.length - 1) : currentIndex + step;
        const nextIndex = findEnabledIndex(items, startIndex, step, true);

        if (nextIndex !== -1) {
            activateByKeyboard(items[nextIndex].id);
        }
    };

    const removeActive = (): boolean => {
        if (!activeItem?.onRemove) {
            return false;
        }

        // The removed row disappears from `items`; keep the keyboard position on the row that takes its place.
        const index = findItemIndex(items, activeItem.id);
        const nextIndex = findEnabledIndex(items, index + 1, 1, false);
        const neighbourIndex = nextIndex === -1 ? findEnabledIndex(items, index - 1, -1, false) : nextIndex;

        activateByKeyboard(neighbourIndex === -1 ? null : items[neighbourIndex].id);
        activeItem.onRemove();

        return true;
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        // Keys confirming an IME composition belong to the composition.
        if (event.nativeEvent.isComposing) {
            return;
        }

        switch (event.key) {
            case "ArrowDown":
            case "ArrowUp":
                event.preventDefault();
                if (isOpen) {
                    moveActive(event.key === "ArrowDown" ? 1 : -1);
                } else {
                    requestOpenChange(true, "arrow");
                }
                return;
            case "Home":
            case "End": {
                // With text in the field the keys move the caret (editable combobox); Shift/modifier variants keep their native selection meaning.
                if (!isOpen || inputValue !== "" || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
                    return;
                }
                const edgeIndex = event.key === "Home" ? findEnabledIndex(items, 0, 1, false) : findEnabledIndex(items, items.length - 1, -1, false);
                if (edgeIndex === -1) {
                    return;
                }
                event.preventDefault();
                activateByKeyboard(items[edgeIndex].id);
                return;
            }
            case "Enter":
                event.preventDefault();
                submit();
                return;
            case "Escape":
                if (!isOpen) {
                    return;
                }
                event.preventDefault();
                // An enclosing modal must not close together with the panel.
                event.stopPropagation();
                requestOpenChange(false, "escape");
                return;
            case "Tab":
                if (isOpen) {
                    requestOpenChange(false, "tab");
                }
                return;
            case "Delete":
                if ((event.shiftKey || inputValue === "") && removeActive()) {
                    event.preventDefault();
                }
                return;
        }
    };

    const getInputProps = <TProps extends SearchComboboxInputUserProps>(userProps?: TProps) => {
        const {onChange, onKeyDown, onFocus, onClick, onBlur, ...restProps} = (userProps ?? {}) as TProps;

        const inputProps: ISearchComboboxInputDomProps = {
            id: inputId,
            role: "combobox",
            autoComplete: "off",
            "aria-autocomplete": "list",
            "aria-expanded": isOpen,
            "aria-controls": listboxId,
            "aria-activedescendant": activeId !== null ? getOptionDomId(activeId) : undefined,
            onChange: (event) => {
                onChange?.(event);
                if (!event.defaultPrevented) {
                    requestOpenChange(true, "typing");
                }
            },
            onKeyDown: (event) => {
                onKeyDown?.(event);
                if (!event.defaultPrevented) {
                    handleKeyDown(event);
                }
            },
            onFocus: (event) => {
                onFocus?.(event);
                if (!event.defaultPrevented) {
                    requestOpenChange(true, "focus");
                }
            },
            onClick: (event) => {
                onClick?.(event);
                if (!event.defaultPrevented) {
                    requestOpenChange(true, "click");
                }
            },
            onBlur: (event: FocusEvent<HTMLInputElement>) => {
                onBlur?.(event);
                // No related target means the window lost focus or a non-focusable spot was clicked - the pointer
                // listener handles the latter, the former keeps the panel for when the user comes back.
                if (!event.defaultPrevented && event.relatedTarget && !isInsideRefs(event.relatedTarget, latestRef.current.options.rootRefs)) {
                    requestOpenChange(false, "outside");
                }
            }
        };

        return {...restProps, ...inputProps} as Omit<TProps, keyof ISearchComboboxInputDomProps> & ISearchComboboxInputDomProps;
    };

    const getListboxProps = (): ISearchComboboxListboxDomProps => ({
        id: listboxId,
        role: "listbox",
        tabIndex: -1,
        "aria-label": listboxLabel,
        onMouseDown: preventDefault
    });

    const getOptionProps = (id: string): ISearchComboboxOptionDomProps => {
        const item = itemsById.get(id);
        const optionProps: ISearchComboboxOptionDomProps = {
            id: getOptionDomId(id),
            role: "option",
            "aria-selected": activeId === id,
            onMouseDown: preventDefault,
            onMouseMove: (event: MouseEvent<HTMLElement>) => {
                if (!item || item.isDisabled) {
                    return;
                }

                // A list scrolling under a still cursor may emit mousemove with unchanged coordinates - not a hover.
                const lastPointer = lastPointerRef.current;
                if (lastPointer && lastPointer.x === event.clientX && lastPointer.y === event.clientY) {
                    return;
                }
                lastPointerRef.current = {x: event.clientX, y: event.clientY};

                if (activeId !== id || !isUserControlled) {
                    setUserActive({id, inputValue, source: "pointer"});
                }
            },
            onClick: () => {
                if (item && !item.isDisabled) {
                    item.onSelect();
                }
            }
        };

        // role="option" does not support aria-expanded (ARIA 1.2); the consumer names the state in the row's text.
        if (item?.isExpanded !== undefined) {
            optionProps["data-expanded"] = item.isExpanded;
        }
        if (item?.isDisabled) {
            optionProps["aria-disabled"] = true;
        }

        return optionProps;
    };

    const getGroupProps = (labelId: string): ISearchComboboxGroupDomProps => ({role: "group", "aria-labelledby": labelId});

    return {
        inputId,
        listboxId,
        activeId,
        setActiveId,
        reset,
        submit,
        getInputProps,
        getListboxProps,
        getOptionProps,
        getGroupProps,
        getGroupLabelId,
        getOptionDomId
    };
};
